import { useEffect, useState } from 'react';

import { inferenceModelParams } from '@playbooks/api';
import { FarIcons } from '@playbooks/icons';
import { SearchField } from '@playbooks/molecules/forms';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
	ComposerActionSection,
} from 'components/composer/composer-actions-components';
import { useStore } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody, FlowView } from 'utils/flow';

const formatExampleCredits = item => {
	if (item.exampleCredits === null || item.exampleCredits === undefined) return '';
	const credits = Number(item.exampleCredits).toLocaleString(undefined, { maximumFractionDigits: 2 });
	return `${item.pricingLabel ? `${item.pricingLabel} · ` : ''}~${credits} credits`;
};

const ExamplePricingNote = () => (
	<p className='type-meta px-3 py-2 text-foreground-subtle'>Example: 100K uncached input + 10K output.</p>
);

const ComposerActionsModel = ({ disabled = false, model, open = false, onClose, onPrev = undefined, onSelect }) => {
	const [inferenceModel, setInferenceModel] = useState<any>({});
	const [inferenceModels, setInferenceModels] = useState([]);
	const [view, setView] = useState('preferred');
	const [direction, setDirection] = useState('forward');
	const [preferredQuery, setPreferredQuery] = useState('');
	const [advancedQuery, setAdvancedQuery] = useState('');
	const store = useStore();

	// Computed
	const matchesQuery = (item, query) => {
		const search = query.toLowerCase();
		return item.name?.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search);
	};
	const playbooksModels = inferenceModels
		.filter((item: any) => item.type === 'basic')
		.sort((a: any, b: any) => a.position - b.position);
	const providerModels = inferenceModels.filter((item: any) => item.type === 'provider');
	const filteredPlaybooksModels = playbooksModels.filter(item => matchesQuery(item, preferredQuery));
	const filteredProviderModels = providerModels.filter(item => matchesQuery(item, advancedQuery));
	const selectedProviderName =
		inferenceModel.type === 'provider' ? inferenceModel.name?.replace('Playbooks ', '') : undefined;

	// Hooks
	useEffect(() => {
		fetchData();
	}, []);
	useEffect(() => {
		if (!open) return;
		setView('preferred');
		setDirection('forward');
		setPreferredQuery('');
		setAdvancedQuery('');
	}, [open]);
	useEffect(() => {
		if (model?.id) setInferenceModel(model);
	}, [model?.id]);

	// Actions
	const [fetchData, loading, error] = useInit(async () => {
		if (inferenceModels.length) return;
		const response = await store.query({ url: `/inference-models`, params: inferenceModelParams });
		setInferenceModels(response.data);
	});

	// Methods
	const onClick = item => {
		setInferenceModel(item);
		onSelect?.(item);
		onClose?.();
	};
	const onAdvanced = () => {
		setDirection('forward');
		setView('advanced');
	};
	const onBack = () => {
		setDirection('backward');
		setView('preferred');
	};
	const renderModel = item => {
		const active = inferenceModel.id === item.id;
		return (
			<ComposerActionRow
				key={item.id}
				icon={FarIcons.farMicrochip}
				label={item.name.replace('Playbooks ', '')}
				value={formatExampleCredits(item)}
				selected={active}
				disabled={disabled}
				trailing={
					active ? (
						<StructuralIcon icon={FarIcons.farCircleCheck} className='size-3.5 text-success' aria-hidden />
					) : undefined
				}
				onClick={() => onClick(item)}
			/>
		);
	};

	// Render
	return (
		<FlowView key={view} direction={direction}>
			{view === 'preferred' ? (
				<>
					<ComposerActionHeader onPrev={onPrev}>
						<SearchField
							value={preferredQuery}
							placeholder='Search models...'
							className='h-8 grow'
							onChange={event => setPreferredQuery(event.target.value)}
							onClear={() => setPreferredQuery('')}
						/>
					</ComposerActionHeader>
					<FlowBody>
						{loading ? (
							<ComposerActionListSkeleton count={5} />
						) : error ? (
							<ComposerActionFeedback
								icon='triangle-exclamation'
								title='Models unavailable'
								text={error?.message || 'Models could not be loaded.'}
							/>
						) : (
							<>
								{filteredPlaybooksModels.length ? (
									<ComposerActionSection label='Preferred'>
										{filteredPlaybooksModels.map(renderModel)}
										<ExamplePricingNote />
									</ComposerActionSection>
								) : (
									<p className='type-ui p-4 text-foreground-body'>No preferred models match your search.</p>
								)}
								{providerModels.length ? (
									<ComposerActionSection className='border-t'>
										<ComposerActionRow
											icon={FarIcons.farGear}
											label='Advanced'
											value={selectedProviderName}
											disabled={disabled}
											onClick={onAdvanced}
										/>
									</ComposerActionSection>
								) : null}
							</>
						)}
					</FlowBody>
				</>
			) : (
				<>
					<ComposerActionHeader onPrev={onBack}>
						<SearchField
							value={advancedQuery}
							placeholder='Search advanced models...'
							className='h-8 grow'
							onChange={event => setAdvancedQuery(event.target.value)}
							onClear={() => setAdvancedQuery('')}
						/>
					</ComposerActionHeader>
					<FlowBody>
						{filteredProviderModels.length ? (
							<ComposerActionSection label='Advanced'>
								{filteredProviderModels.map(renderModel)}
								<ExamplePricingNote />
							</ComposerActionSection>
						) : (
							<p className='type-ui p-4 text-foreground-body'>No advanced models match your search.</p>
						)}
					</FlowBody>
				</>
			)}
		</FlowView>
	);
};
export { ComposerActionsModel };
